import React from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../redux/root';

const clamp = (n: number) => Math.min(100, Math.max(0, n));

const ProgressBar = () => {
  const { percent, completedCount, allCount, rejectCount } = useSelector((s: RootState) => s.documents.PercentComplete);
  const pct = clamp(Number(percent) || 0);
  const total = Math.max(0, (allCount || 0) - (rejectCount || 0));
  const done = completedCount || 0;
  const tone = pct >= 100 ? 'complete' : pct >= 50 ? 'mid' : 'start';

  return (
    <Wrapper>
      <TopRow>
        <Meta>
          <MetaLabel>Progress</MetaLabel>
          <MetaText>
            <Strong>{done}</Strong> of <Strong>{total}</Strong> documents completed
          </MetaText>
        </Meta>
        <PercentBadge $tone={tone}>{pct}%</PercentBadge>
      </TopRow>
      <Track role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
        <Fill $pct={pct} $tone={tone} />
      </Track>
    </Wrapper>
  );
};

export default ProgressBar;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Meta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const MetaLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const MetaText = styled.span`
  font-size: 0.875rem;
  color: var(--neutral-700);
  line-height: 1.4;
`;

const Strong = styled.span`
  color: var(--mainText);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

const toneColor = (tone: 'start' | 'mid' | 'complete') => {
  if (tone === 'complete') return 'var(--success-500, #2f9e5e)';
  if (tone === 'mid') return 'var(--primary-600)';
  return 'var(--primary-500, #1476a8)';
};

const toneBg = (tone: 'start' | 'mid' | 'complete') => {
  if (tone === 'complete') return 'var(--success-50, #e8f6ee)';
  return 'var(--primary-50)';
};

const toneFg = (tone: 'start' | 'mid' | 'complete') => {
  if (tone === 'complete') return 'var(--success-700, #196a3b)';
  return 'var(--primary-700)';
};

const PercentBadge = styled.span<{ $tone: 'start' | 'mid' | 'complete' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 52px;
  height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  background: ${({ $tone }) => toneBg($tone)};
  color: ${({ $tone }) => toneFg($tone)};
  border: 1px solid ${({ $tone }) => ($tone === 'complete' ? 'var(--success-100, #c5e7d2)' : 'var(--primary-100, #cfe6f3)')};
  font-size: 0.8125rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

const Track = styled.div`
  position: relative;
  width: 100%;
  height: 10px;
  background: var(--neutral-100);
  border-radius: 999px;
  overflow: hidden;
`;

const Fill = styled.div<{ $pct: number; $tone: 'start' | 'mid' | 'complete' }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: ${({ $tone }) => toneColor($tone)};
  border-radius: 999px;
  transition: width 0.4s ease, background-color 0.3s ease;
`;
